import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { Messages } from './i18n/de.ts';

/**
 * Routes, written out rather than generated from files.
 *
 * vue-router 5 can derive routes from a `pages/` directory. For five routes an
 * explicit table is shorter than the convention that replaces it, and it keeps
 * the one thing a reader of this chapter needs in one place: which URL shows
 * which view, and which parts of the URL are state.
 *
 * Views are lazy: each is its own chunk, fetched on first visit. The device
 * chart pulls in ECharts and the map pulls in Leaflet (chapters 22 and 23), and
 * an operator who only ever opens the overview should not download either.
 */

declare module 'vue-router' {
  interface RouteMeta {
    /** Key under `pages` in the messages, for the document title. */
    title: keyof Messages['pages'];
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'overview',
    component: () => import('./views/OverviewView.vue'),
    meta: { title: 'overview' },
  },
  {
    // The list's filters, sort and cursor live in the query string, which is
    // chapter 17's point carried into the browser: a reload, a back button or
    // a link sent to a colleague shows the same list.
    path: '/devices',
    name: 'devices',
    component: () => import('./views/DevicesView.vue'),
    meta: { title: 'devices' },
  },
  {
    path: '/devices/:id',
    name: 'device',
    component: () => import('./views/DeviceView.vue'),
    props: true,
    meta: { title: 'device' },
  },
  {
    // A twin's page is its dashboard, keyed on its id: niotix's nested
    // dashboards navigate on dtwin.id (FIDELITY.md M10).
    path: '/twins/:id',
    name: 'twin',
    component: () => import('./views/TwinView.vue'),
    props: true,
    meta: { title: 'twin' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('./views/NotFoundView.vue'),
    meta: { title: 'notFound' },
  },
];

/**
 * Build the router. A function, not a module-level `const`, and the difference
 * is a bug that shipped for one afternoon.
 *
 * `createWebHistory()` reads `window.location` when it is called. As a module
 * constant it was called at import time, before keycloak-js had processed the
 * login redirect, so it captured a URL still carrying `#state=…&code=…`.
 * keycloak-js then removed the fragment, and the router's first navigation put
 * it straight back. The authorization code sat in the address bar and the
 * history, and a reload tried to redeem a code that had already been used.
 * Creating the router after the session exists means it sees the clean URL.
 */
export function createAppRouter() {
  return createRouter({
    history: createWebHistory(),
    routes,
    // Back and forward restore the scroll position; a new page starts at the top.
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  });
}
